import { handleAuthError, isAuthError } from './utils/authUtils';

import { TokenService } from '../application/services/TokenService';
import axios from '@/common/infrastructure/axios';
import { withScope, captureException } from '@sentry/react';

/**
 * Set up axios auth interceptors for handling authentication
 * @param tokenService The token service to use for retrieving/managing the token
 */
export const setupAxiosAuthInterceptors = (tokenService: TokenService): void => {
  // Request interceptor to add authentication token
  axios.interceptors.request.use(
    async (config) => {
      const token = tokenService.getToken();
      if (token) {
        // Ensure headers object exists
        config.headers = config.headers || {};
        config.headers['Authorization'] = `rca ${token}`;
      }

      // Attach selected client mapping header so backend can scope requests to the active client.
      // This is read per-request, so switching clients immediately affects subsequent API calls.
      try {
        const storedClient = localStorage.getItem('selectedClient');
        const parsedClient = storedClient ? JSON.parse(storedClient) : null;
        const mappingID = parsedClient?.globalUserMappingID;

        config.headers = config.headers || {};
        if (mappingID !== null && mappingID !== undefined) {
          config.headers['MappingID'] = String(mappingID);
        } else {
          delete config.headers['MappingID'];
        }
      } catch {
        config.headers = config.headers || {};
        delete config.headers['MappingID'];
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor to handle auth errors
  axios.interceptors.response.use(
    (response) => {
      // Check for auth errors in JSON response even with 200 status code
      // This handles cases where the backend returns auth errors with 200 OK
      const data = response.data;

      // Check for GraphQL-style auth errors
      if (data && data.errors && Array.isArray(data.errors)) {
        for (const err of data.errors) {
          withScope((scope) => {
            scope.setExtra('error_type', 'graphql-auth-error');
            captureException(err);
          });

          if (isAuthError(err)) {
            console.error('Authentication error in response:', err);
            handleAuthError();
            return Promise.reject(new Error('Authentication failed'));
          }
        }
      }
      return response;
    },
    (error) => {
      if (isAuthError(error)) {
        handleAuthError();
      }
      withScope((scope) => {
        scope.setExtra('error_type', 'axios-auth-error');
        captureException(error);
      });
      return Promise.reject(error);
    },
  );
};
